import { useState, useEffect, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useCodebaseStore, EditorContext } from "@/store/zustand/codebaseStore";
import useLayoutStore, { LeftTab } from "@/store/zustand/layoutStore";
import { useCanvasId } from "@/utils/utils";
import { useSupabase } from "@/components/contexts/DatabaseSyncWrapper";
import { useElementMetadata } from "@/components/contexts/ElementMetadataContext";
import { JsxElementType, type JsxElement } from "@/utils/jsxElementTypes";
import {
  initializeQueue,
  markElementsWithForce,
  startBulkGeneration,
  clearBulkGenerated,
  clearQueue,
  type BulkGeneratedPreview,
  type ComponentFailure,
} from "@/store/metadataSlice";
import { toast } from "sonner";
import { CircleX } from "lucide-react";
import { AssetItem } from "./AssetItem";

interface BulkPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface RootState {
  metadata: {
    elementsToFetch: string[];
    isFetching: boolean;
    bulkGenerated: BulkGeneratedPreview[];
    queueFailureState: {
      hasFailures: boolean;
      consecutiveFailures: number;
      lastFailureMessage?: string;
      failedAt?: number;
      failedComponents: ComponentFailure[];
    };
  };
}

// Convert preview to JsxElement format
const previewToElement = (preview: BulkGeneratedPreview): JsxElement => ({
  codebaseId: preview.codebaseId,
  componentName: preview.componentName,
  filename: preview.filename,
  fileHash: preview.fileHash,
  type: JsxElementType.StorybookComponent,
  key: preview.codebaseId,
});

const FailuresDetailView: React.FC<{
  failedComponents: ComponentFailure[];
  onBack: () => void;
  onNavigateToFile: (filename: string) => void;
  canvasId: string;
}> = ({ failedComponents, onBack, onNavigateToFile, canvasId }) => {
  return (
    <div className="space-y-3 w-full">
      <div className="flex items-center gap-2">
        <Button
          onClick={onBack}
          variant="ghost"
          size="sm"
          className="px-2 text-xs"
        >
          ← Back
        </Button>
      </div>

      <div className="max-h-80 overflow-y-auto space-y-2">
        {failedComponents.map((failure) => (
          <div
            key={failure.codebaseId}
            className="p-3 border border-border-primary rounded-md w-[95%] overflow-hidden mx-auto"
          >
            <div className="flex items-start gap-2 mb-2">
              <CircleX className="w-4 h-4 text-icons-danger flex-shrink-0 mt-0.5" />
              <div className="flex items-start justify-between gap-2 flex-1 min-w-0">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-text-primary text-sm truncate">
                    {failure.componentName}
                  </div>
                  <div className="text-text-secondary text-xs truncate">
                    {failure.filename.replace(
                      new RegExp(
                        `.*${canvasId.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`
                      ),
                      ""
                    )}
                  </div>
                </div>
                <Button
                  onClick={() => onNavigateToFile(failure.filename)}
                  variant="outline"
                  size="sm"
                  className="text-xs px-2 py-1 h-6 flex-shrink-0 border-secondary-500 text-text-primary hover:bg-secondary-700"
                >
                  Open File
                </Button>
              </div>
            </div>
            <div className="ml-6 text-text-secondary text-xs leading-tight break-all overflow-wrap-anywhere bg-background-secondary p-2 rounded border border-secondary-700 max-w-full overflow-hidden">
              {failure.errorMessage}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const FailedComponentsList: React.FC<{
  failedComponents: ComponentFailure[];
  onViewDetails?: () => void;
}> = ({ failedComponents, onViewDetails }) => {
  if (failedComponents.length === 0) return null;

  return (
    <div className="space-y-2 w-full">
      <div className="max-h-32 overflow-y-auto space-y-1 w-full">
        <div className="p-2 text-xs border border-border-primary rounded w-full overflow-hidden">
          <div className="flex items-start gap-2">
            <CircleX className="w-4 h-4 text-icons-danger flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              {`${failedComponents.length} preview${
                failedComponents.length > 1 ? "s" : ""
              } couldn’t be generated.`}
              <div className="text-text-secondary text-xs">
                Some components may be invalid or have other issues. Click the
                button below to view more details.
              </div>
              <button
                className="text-primary-0 hover:underline hover:text-primary-100 text-xs cursor-pointer mt-2"
                onClick={onViewDetails}
                type="button"
              >
                View Details
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const GeneratedPreviewsCarousel: React.FC<{
  bulkGenerated: BulkGeneratedPreview[];
  isGenerating: boolean;
}> = ({ bulkGenerated, isGenerating }) => {
  const recentElements = bulkGenerated.slice(-3).map(previewToElement);
  const placeholderCount = Math.max(
    0,
    4 - recentElements.length - (isGenerating ? 1 : 0)
  );

  return (
    <div className="p-3 bg-secondary-50 rounded-lg w-full overflow-hidden">
      <div className="flex items-center justify-center gap-2 mb-3 overflow-x-auto">
        {recentElements.map((element) => (
          <div key={element.codebaseId} className="w-20 flex-shrink-0">
            <AssetItem element={element} selected={false} draggable={false} />
          </div>
        ))}

        {isGenerating && (
          <div className="w-20 h-[100px] bg-secondary-200 rounded-lg animate-pulse flex-shrink-0" />
        )}

        {Array.from({ length: placeholderCount }, (_, index) => (
          <div
            key={`placeholder-${Date.now()}-${index}`}
            className="w-20 h-[100px] bg-secondary-200 rounded-lg animate-pulse flex-shrink-0"
          />
        ))}
      </div>
    </div>
  );
};

const GenerationCompleteSummary: React.FC<{
  bulkGenerated: BulkGeneratedPreview[];
  failedComponents: ComponentFailure[];
  onClose: () => void;
  onViewFailures: () => void;
}> = ({ bulkGenerated, failedComponents, onClose, onViewFailures }) => {
  const generatedElements = bulkGenerated.map(previewToElement);

  return (
    <div className="space-y-3 w-full">
      {generatedElements.length > 0 && (
        <div className="max-h-64 overflow-y-auto w-full">
          <div className="flex flex-wrap justify-center gap-2 p-3 rounded-lg overflow-hidden">
            {generatedElements.slice(0, 50).map((element) => (
              <div key={element.codebaseId} className="w-20 flex-shrink-0">
                <AssetItem
                  element={element}
                  selected={false}
                  draggable={false}
                />
              </div>
            ))}
            {generatedElements.length > 50 && (
              <div className="w-20 h-12 bg-secondary-200 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-xs text-text-secondary">
                  +{generatedElements.length - 50}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      <FailedComponentsList
        failedComponents={failedComponents}
        onViewDetails={onViewFailures}
      />

      <div className="flex gap-3 w-full">
        <Button
          onClick={onClose}
          variant="secondary"
          size="sm"
          className="flex-1"
        >
          Close
        </Button>
      </div>
    </div>
  );
};

export const BulkPreviewModal: React.FC<BulkPreviewModalProps> = ({
  isOpen,
  onClose,
}) => {
  const dispatch = useDispatch();
  const canvasId = useCanvasId();
  const [isGenerating, setIsGenerating] = useState(false);
  const [showCarousel, setShowCarousel] = useState(false);
  const [showFailuresDetail, setShowFailuresDetail] = useState(false);
  const hasRefetchedOnOpen = useRef(false);

  const { ungeneratedElements } = useElementMetadata();

  const hardOpenFile = useCodebaseStore((state) => state.hardOpenFile);
  const setNewLayout = useLayoutStore((state) => state.setNewLayout);
  const { triggerRefetchElementMetadata } = useSupabase();

  // Redux selectors
  const elementsToFetch = useSelector(
    (state: RootState) => state.metadata.elementsToFetch
  );
  const isFetching = useSelector(
    (state: RootState) => state.metadata.isFetching
  );
  const bulkGenerated = useSelector(
    (state: RootState) => state.metadata.bulkGenerated
  );
  const queueFailureState = useSelector(
    (state: RootState) => state.metadata.queueFailureState
  );

  const totalElements = ungeneratedElements.length;
  const completedElements = Math.min(bulkGenerated.length, totalElements);
  const remainingElements = Math.max(0, totalElements - completedElements);
  const progress =
    totalElements > 0 ? (completedElements / totalElements) * 100 : 0;
  const estimatedTimeMinutes = Math.ceil(totalElements * 0.1);

  const handleStartGeneration = () => {
    if (totalElements === 0) {
      toast.info("All previews are already generated!");
      return;
    }

    setIsGenerating(true);
    setShowCarousel(true);

    dispatch(startBulkGeneration(ungeneratedElements));
    dispatch(markElementsWithForce(ungeneratedElements));
    dispatch(initializeQueue(ungeneratedElements));

    toast.success(`Started generating ${totalElements} previews`);
  };

  const handleAbort = () => {
    dispatch(clearQueue());
    dispatch(clearBulkGenerated());
    setIsGenerating(false);
    setShowCarousel(false);
    toast.info("Bulk generation aborted");
  };

  const handleClose = () => {
    if (isGenerating) {
      handleAbort();
    } else {
      dispatch(clearBulkGenerated());
    }
    onClose();
  };

  // File navigation handler
  const handleNavigateToFile = (filename: string) => {
    // First, switch to the FileSelector tab to show the file tree
    setNewLayout(LeftTab.FileSelector);

    // Then open the file in the editor
    hardOpenFile({
      filename: filename,
      context: EditorContext.Code,
    });

    onClose(); // Close modal after navigation
  };

  // Handle modal state changes
  useEffect(() => {
    if (!isOpen) {
      setIsGenerating(false);
      setShowCarousel(false);
      hasRefetchedOnOpen.current = false;
    } else if (
      isOpen &&
      !hasRefetchedOnOpen.current &&
      triggerRefetchElementMetadata
    ) {
      triggerRefetchElementMetadata();
      hasRefetchedOnOpen.current = true;
    }
  }, [isOpen, triggerRefetchElementMetadata]);

  // Monitor generation completion
  useEffect(() => {
    if (isGenerating && elementsToFetch.length === 0 && !isFetching) {
      setIsGenerating(false);
    }
  }, [isGenerating, elementsToFetch.length, isFetching]);

  // Show failures detail view
  if (showFailuresDetail) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 mb-4">
              Component Failures
            </DialogTitle>
          </DialogHeader>

          <FailuresDetailView
            failedComponents={queueFailureState.failedComponents}
            onBack={() => setShowFailuresDetail(false)}
            onNavigateToFile={handleNavigateToFile}
            canvasId={canvasId}
          />
        </DialogContent>
      </Dialog>
    );
  }

  // Show progress view during generation
  if (showCarousel) {
    return (
      <Dialog
        open={isOpen}
        onOpenChange={isGenerating ? undefined : handleClose}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 mb-4">
              {isGenerating ? "Generating Previews..." : "Previews Generated!"}
            </DialogTitle>
            {isGenerating && (
              <DialogDescription className="text-xs text-text-secondary">
                {completedElements} of {totalElements} previews generated
              </DialogDescription>
            )}
          </DialogHeader>

          {isGenerating && (
            <>
              <div className="space-y-3 w-full">
                <Progress value={progress} className="w-full h-2" />
                <div className="flex items-center justify-between text-xs text-text-secondary w-full">
                  <span>{remainingElements} remaining</span>
                  <span>{Math.round(progress)}% complete</span>
                </div>
              </div>
              <GeneratedPreviewsCarousel
                bulkGenerated={bulkGenerated}
                isGenerating={isGenerating}
              />

              <FailedComponentsList
                failedComponents={queueFailureState.failedComponents}
                onViewDetails={() => setShowFailuresDetail(true)}
              />

              <div className="flex gap-3 w-full">
                <Button
                  onClick={handleAbort}
                  variant="destructive"
                  size="sm"
                  className="flex-1"
                >
                  Abort Generation
                </Button>
              </div>
            </>
          )}

          {!isGenerating && (
            <GenerationCompleteSummary
              bulkGenerated={bulkGenerated}
              failedComponents={queueFailureState.failedComponents}
              onClose={onClose}
              onViewFailures={() => setShowFailuresDetail(true)}
            />
          )}
        </DialogContent>
      </Dialog>
    );
  }

  // Show initial setup view
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 mb-4">
            Bulk Generate Previews
          </DialogTitle>

          {/* Show previous failures if any exist */}
          {queueFailureState.failedComponents.length > 0 && (
            <FailedComponentsList
              failedComponents={queueFailureState.failedComponents}
              onViewDetails={() => setShowFailuresDetail(true)}
            />
          )}
          <DialogDescription className="text-xs text-text-secondary">
            Some components may not load a preview right away - if you don’t see
            one, click the button to start generating.{" "}
            {totalElements > 0 &&
              `This might take ~${estimatedTimeMinutes} minute${
                estimatedTimeMinutes > 1 ? "s" : ""
              }.`}{" "}
            Please keep this window open - closing it will cancel the process.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-3 w-full">
          {totalElements === 0 ? (
            <div className="text-xs text-text-secondary flex-1 text-center">
              You're all caught up!
            </div>
          ) : (
            <Button
              onClick={handleStartGeneration}
              disabled={totalElements === 0}
              className="flex items-center gap-2 flex-1"
              variant="primary"
              size="sm"
            >
              Generate {totalElements} Previews
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
